import type { initializeB24Frame } from "@bitrix24/b24jssdk";
import { JOURNAL_ENTITY_KEY, JOURNAL_PROPERTIES, type StorageRegion } from "@shared";

export type StepStatus = "pending" | "active" | "done" | "warning" | "error";
export type StepId = "connect" | "workspace" | "credentials" | "activating";
export type AppState = "running" | "done" | "partial" | "error" | "dev";

export interface ErrorInfo {
  title: string;
  detail: string;
  raw?: string;
}

export interface InstallRoutineCallbacks {
  setStep: (id: StepId, status: StepStatus) => void;
  setErrorInfo: (err: ErrorInfo) => void;
  setWarnMsg: (msg: string) => void;
  setAppState: (state: AppState) => void;
  isCancelled: () => boolean;
}

export const CRITICAL_ERROR_MSGS: Partial<Record<StepId, ErrorInfo>> = {
  workspace: {
    title: "Could not prepare workspace",
    detail:
      "We were unable to create the required data entities on your portal. " +
      'This is usually caused by insufficient app permissions — ensure the app has the "entity" scope. ' +
      "Try reinstalling or contact support.",
  },
  credentials: {
    title: "Could not save credentials",
    detail:
      "We were unable to store your access tokens securely. Please try reinstalling the app. " +
      "If the problem persists, contact your Bitrix24 administrator.",
  },
};

export async function runInstallRoutine(
  b24: Awaited<ReturnType<typeof initializeB24Frame>>,
  callbacks: InstallRoutineCallbacks,
  region?: StorageRegion,
): Promise<void> {
  const { setStep, setErrorInfo, setWarnMsg, setAppState, isCancelled } = callbacks;

  const authDataMaybe = b24.auth.getAuthData();
  if (!authDataMaybe) {
    setStep("connect", "error");
    setErrorInfo({
      title: "No credentials received",
      detail: "Bitrix24 did not provide authentication data. Please try reinstalling the app.",
    });
    setAppState("error");
    return;
  }

  const authData = authDataMaybe as {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    member_id: string;
    server_endpoint?: string;
    scope?: string;
    user_id?: string | number;
    [key: string]: unknown;
  };

  setStep("connect", "done");
  if (isCancelled()) return;

  const domain = b24.getTargetOrigin().replace(/^https?:\/\//, "");

  // ── Step 2: Workspace — all B24 SDK REST calls ─────────────────────────
  setStep("workspace", "active");

  let catalogIblockId: number | null = null;
  let offersIblockId: number | null = null;
  let workspaceWarning = false;

  // 2a. entity.get → entity.add → property.add
  try {
    let journalExists = false;
    try {
      const entityGetResult = await b24.actions.v2.call.make({
        method: "entity.get",
        params: {},
      });
      const entities =
        (entityGetResult.getData() as { result?: Array<{ ENTITY: string }> })?.result ?? [];
      journalExists = entities.some((e) => e.ENTITY === JOURNAL_ENTITY_KEY);
    } catch {
      journalExists = false;
    }

    if (!journalExists) {
      const addResult = await b24.actions.v2.call.make({
        method: "entity.add",
        params: {
          ENTITY: JOURNAL_ENTITY_KEY,
          NAME: "Commission Journals",
          ACCESS: { AU: "W" },
        },
      });

      if (!addResult.isSuccess) {
        const recheckResult = await b24.actions.v2.call.make({
          method: "entity.get",
          params: {},
        });
        const recheckEntities =
          (recheckResult.getData() as { result?: Array<{ ENTITY: string }> })?.result ?? [];
        if (!recheckEntities.some((e) => e.ENTITY === JOURNAL_ENTITY_KEY)) {
          throw new Error(
            `entity.add failed (isSuccess: false). Ensure the app has the "entity" scope in the Bitrix24 marketplace.`,
          );
        }
      }

      const propCalls = Object.fromEntries(
        JOURNAL_PROPERTIES.map((prop, i) => [
          `prop_${i}`,
          {
            method: "entity.item.property.add",
            params: { ENTITY: JOURNAL_ENTITY_KEY, ...prop },
          },
        ]),
      );
      await b24.actions.v2.batch.make({
        calls: propCalls,
        options: { isHaltOnError: false },
      });
    }
  } catch (err) {
    if (isCancelled()) return;
    setStep("workspace", "error");
    setErrorInfo({
      ...(CRITICAL_ERROR_MSGS.workspace ?? {
        title: "Could not prepare workspace",
        detail: "Failed to create data entities. Check permissions.",
      }),
      raw: err instanceof Error ? err.message : String(err),
    });
    setAppState("error");
    return;
  }

  if (isCancelled()) return;

  // 2b. catalog.catalog.list
  try {
    const catalogResult = await b24.actions.v2.call.make({
      method: "catalog.catalog.list",
      params: { select: ["id", "iblockId", "productIblockId"] },
    });
    const catalogs =
      (
        catalogResult.getData() as {
          result?: Array<{ id: number; iblockId: number; productIblockId?: number | null }>;
        }
      )?.result ?? [];
    if (catalogs.length > 0) {
      const mainCatalog =
        catalogs.find((cat) => !cat.productIblockId || cat.productIblockId === 0) ?? catalogs[0]!;
      const offersCatalog = catalogs.find(
        (cat) =>
          cat.productIblockId != null &&
          cat.productIblockId > 0 &&
          cat.productIblockId === mainCatalog.iblockId,
      );
      catalogIblockId = mainCatalog.iblockId;
      offersIblockId = offersCatalog?.iblockId ?? null;
    }
  } catch {
    console.warn("[install-routine] catalog.catalog.list failed (non-fatal)");
  }

  if (isCancelled()) return;

  // 2c. event.bind
  try {
    const handlerBase = `${window.location.origin}/api/events`;
    await b24.actions.v2.batch.make({
      calls: {
        bind_install: {
          method: "event.bind",
          params: { event: "ONAPPINSTALL", handler: `${handlerBase}/install` },
        },
        bind_uninstall: {
          method: "event.bind",
          params: { event: "ONAPPUNINSTALL", handler: `${handlerBase}/uninstall` },
        },
      },
      options: { isHaltOnError: false },
    });
  } catch (err) {
    workspaceWarning = true;
    console.warn("[install-routine] event.bind failed (non-fatal):", err);
  }

  setStep("workspace", workspaceWarning ? "warning" : "done");
  if (workspaceWarning) {
    setWarnMsg(
      "Event handler registration had a minor issue. The app will continue provisioning automatically when you open it.",
    );
  }
  if (isCancelled()) return;

  // ── Step 3: Credentials ─────────────────────────
  setStep("credentials", "active");

  try {
    const res = await fetch("/api/oauth/install", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        memberId: authData.member_id,
        domain,
        accessToken: authData.access_token,
        refreshToken: authData.refresh_token,
        expiresIn: authData.expires_in,
        scope: typeof authData.scope === "string" ? authData.scope : undefined,
        userId: authData.user_id != null ? String(authData.user_id) : undefined,
        serverEndpoint:
          typeof authData.server_endpoint === "string" ? authData.server_endpoint : undefined,
        catalogIblockId,
        offersIblockId,
        region,
      }),
    });
    const apiResponse = (await res.json()) as { success: boolean; error?: unknown };
    if (!apiResponse.success) {
      if (isCancelled()) return;
      const rawErr = apiResponse.error;
      const errDetail =
        typeof rawErr === "string"
          ? rawErr
          : rawErr != null && typeof rawErr === "object" && "message" in rawErr
            ? String(rawErr.message)
            : "Unknown error";
      setStep("credentials", "error");
      setErrorInfo(
        CRITICAL_ERROR_MSGS.credentials ?? {
          title: "Credentials step failed",
          detail: errDetail,
        },
      );
      setAppState("error");
      return;
    }
  } catch (networkErr) {
    if (isCancelled()) return;
    setStep("credentials", "error");
    setErrorInfo({
      title: "Network error",
      detail:
        "Could not reach the installation server. Please check your connection and try again.",
      raw: networkErr instanceof Error ? networkErr.message : String(networkErr),
    });
    setAppState("error");
    return;
  }

  setStep("credentials", "done");
  if (isCancelled()) return;

  // ── Step 4: Activating (installFinish) ─────────────────────────
  setStep("activating", "active");

  let activatingOk = true;
  try {
    if (b24.isInstallMode) {
      await b24.installFinish();
    } else {
      // If we are in "repair" mode, the app was already installed but B24 lost the state,
      // so we explicitly call installFinish() regardless of b24.isInstallMode.
      // Or we can just call it always since if it's already finished, it might just no-op or error safely.
      await b24.installFinish();
    }
    setStep("activating", "done");
  } catch (err) {
    activatingOk = false;
    setStep("activating", "warning");
    if (!workspaceWarning) {
      // don't overwrite if there's already a warning msg
      setWarnMsg(
        "Workspace activation had a minor issue. The app will continue provisioning automatically when you open it.",
      );
    }
    console.warn("[install-routine] installFinish() failed (may be re-install):", err);
  }

  if (isCancelled()) return;
  setAppState(activatingOk && !workspaceWarning ? "done" : "partial");
}
